import React from 'react';
import { X } from 'lucide-react';
import type { FeedbackWithUser } from '../../../services/feedbackService';

type AdminFeedbackNotesModalProps = {
  feedback: FeedbackWithUser;
  adminNotes: string;
  onAdminNotesChange: (value: string) => void;
  onClose: () => void;
  onSave: () => void;
  updating: boolean;
  getFilenameFromUrl: (url: string) => string;
};

const AdminFeedbackNotesModal: React.FC<AdminFeedbackNotesModalProps> = ({
  feedback,
  adminNotes,
  onAdminNotesChange,
  onClose,
  onSave,
  updating,
  getFilenameFromUrl,
}) => (
  <div className="admin-feedback-notes-modal-overlay">
    <div className="admin-feedback-notes-modal custom-scrollbar">
      <div className="admin-feedback-notes-modal-header">
        <h2 className="admin-feedback-notes-modal-title">Admin Notes</h2>
        <button type="button" onClick={onClose} className="admin-feedback-notes-modal-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="admin-feedback-notes-modal-body">
        <div className="admin-feedback-notes-modal-feedback-preview">
          <p className="admin-feedback-notes-modal-label">Feedback:</p>
          <p className="admin-feedback-notes-modal-subject">{feedback.subject}</p>
          <p className="admin-feedback-notes-modal-message">{feedback.message}</p>
          {feedback.image_url && (
            <div className="admin-feedback-notes-modal-images">
              {feedback.image_url
                .split(',')
                .map((url) => url.trim())
                .filter((url) => url && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/api/')))
                .map((url, index) => {
                  const imageUrl = url.startsWith('/api/')
                    ? `${process.env.REACT_APP_API_URL || ''}${url}`
                    : url;
                  const fileName = getFilenameFromUrl(imageUrl);
                  return (
                    <div key={index} className="admin-feedback-notes-modal-image-wrap">
                      <img
                        src={imageUrl}
                        alt={`Feedback attachment ${index + 1}`}
                        className="admin-feedback-notes-modal-image"
                        onClick={() => window.open(imageUrl, '_blank', 'noopener,noreferrer')}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <p className="admin-feedback-notes-modal-filename" title={fileName}>
                        {fileName}
                      </p>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
        <div className="admin-feedback-notes-modal-field">
          <label className="admin-feedback-notes-modal-label">Admin Notes</label>
          <textarea
            value={adminNotes}
            onChange={(e) => onAdminNotesChange(e.target.value)}
            rows={5}
            className="admin-feedback-notes-modal-textarea"
            placeholder="Add admin notes here..."
          />
        </div>
        <div className="admin-feedback-notes-modal-actions">
          <button type="button" onClick={onClose} className="admin-feedback-notes-modal-cancel">
            Cancel
          </button>
          <button type="button" onClick={onSave} disabled={updating} className="admin-feedback-notes-modal-save">
            {updating ? 'Saving...' : 'Save Notes'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default AdminFeedbackNotesModal;
